import React from 'react';
import { useThread } from '@assistant-ui/react';

const STALE_LOOKUP = /^tapClientLookup: (Index \d+ out of bounds|Key ".*" not found)/;
const MAX_RETRIES = 3;

const isStale = (error: unknown) => error instanceof Error && STALE_LOOKUP.test(error.message);

/**
 * assistant-ui can draw a message whose row has just left the thread (a running turn's placeholder dropped, the
 * history cleared under a live thread), and its lookup then throws (#1095). That frame draws no messages and the next
 * one draws them again; a lookup that fails three frames running, or any other error, goes on to the page.
 */
export function StaleMessageGuard({ thread, children }: { thread: 'A' | 'B'; children: React.ReactNode }) {
  const count = useThread((t) => t.messages.length);
  return <Guard thread={thread} count={count}>{children}</Guard>;
}

interface GuardProps {
  thread: 'A' | 'B';
  count: number;
  children: React.ReactNode;
}

class Guard extends React.Component<GuardProps, { error: unknown; retries: number }> {
  state = { error: null as unknown, retries: 0 };
  private drawnCount = this.props.count;
  private frame: number | null = null;

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidCatch(error: unknown) {
    if (!isStale(error) || this.state.retries >= MAX_RETRIES || this.frame != null) return;
    console.warn(
      `[ivyChat] thread ${this.props.thread}: a message left the thread while it drew (${this.drawnCount} → ${this.props.count} messages); drawing again (${this.state.retries + 1}/${MAX_RETRIES})`,
      (error as Error).message,
    );
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.setState((s) => ({ error: null, retries: s.retries + 1 }));
    });
  }

  componentDidUpdate() {
    if (this.state.error) return;
    this.drawnCount = this.props.count;
    if (this.state.retries > 0) this.setState({ retries: 0 });
  }

  componentWillUnmount() {
    if (this.frame != null) cancelAnimationFrame(this.frame);
  }

  render() {
    const { error, retries } = this.state;
    if (error && (!isStale(error) || retries >= MAX_RETRIES)) throw error;
    return error ? null : this.props.children;
  }
}
